import { useState } from 'react';
import {
  AppDateField,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppScreenScaffold,
  AppSectionCard,
  AppSectionDivider,
  AppSectionRow,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
  IconButton,
  type IconName,
} from '@core/designsystem';
import type { EventType } from '@core/domain/model';
import { eventTypeLabel } from './month';
import type { EventDetailActions, EventDetailState } from './useEventDetail';
import './calendar.css';

const TYPE_ICON: Record<EventType, IconName> = {
  Hearing: 'gavel',
  Meeting: 'group',
  Task: 'checklist',
  Other: 'event',
};

const TYPES: EventType[] = ['Hearing', 'Meeting', 'Task', 'Other'];

/**
 * Карточка события — «Структура» §9.2.
 *
 * Из блоков, как остальные карточки ([D-36](docs/DECISIONS.md)). Отметки выполнения
 * здесь нет: заседание не «выполняют», оно либо состоялось, либо перенесено. У задачи
 * для этого своя карточка в разделе «Задачи».
 */
export function EventDetailScreen({
  state,
  actions,
  onBack,
  onOpenCase,
  onOpenClient,
}: {
  state: EventDetailState;
  actions: EventDetailActions;
  onBack: () => void;
  onOpenCase: (caseId: string) => void;
  onOpenClient: (clientId: string) => void;
}) {
  const [editing, setEditing] = useState<'title' | 'description' | null>(null);
  const [picking, setPicking] = useState<'type' | 'case' | null>(null);
  const [movingTime, setMovingTime] = useState(false);

  if (state.notFound) {
    return (
      <AppScreenScaffold topBar={<AppTopBar title="Событие" onBack={onBack} />}>
        <AppEmptyState icon="error" text="Событие не найдено" />
      </AppScreenScaffold>
    );
  }

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title={state.title}
          subtitle={state.whenLabel}
          onBack={onBack}
          actions={<IconButton icon="delete" label="Удалить" onClick={() => void actions.remove()} />}
        />
      }
      overlay={
        <>
          {editing ? (
            <TextDialog
              title={editing === 'title' ? 'Название' : 'Описание'}
              value={editing === 'title' ? state.title : state.description}
              onDismiss={() => setEditing(null)}
              onConfirm={(value) => {
                if (editing === 'title') actions.setTitle(value);
                else actions.setDescription(value);
                setEditing(null);
              }}
            />
          ) : null}

          {picking === 'type' ? (
            <AppMenuSheet
              title="Тип события"
              onDismiss={() => setPicking(null)}
              items={TYPES.map((item) => ({
                icon: TYPE_ICON[item],
                label: eventTypeLabel(item),
                onSelect: () => actions.setType(item),
              }))}
            />
          ) : null}

          {picking === 'case' ? (
            <AppMenuSheet
              title="Дело события"
              onDismiss={() => setPicking(null)}
              items={[
                { icon: 'close', label: 'Без дела', onSelect: () => actions.setCase(null) },
                ...state.cases.map((item) => ({
                  icon: 'folder' as const,
                  label: item.title,
                  onSelect: () => actions.setCase(item.id),
                })),
              ]}
            />
          ) : null}

          {movingTime ? (
            <WhenDialog
              startAt={state.startAt}
              onDismiss={() => setMovingTime(false)}
              onConfirm={(at) => {
                actions.setStart(at);
                setMovingTime(false);
              }}
            />
          ) : null}

          {state.isDeleted ? (
            <AppUndoSnackbar
              text="Событие удалено"
              onUndo={() => void actions.undoDelete()}
              onExpire={() => {
                actions.deletionShown();
                onBack();
              }}
            />
          ) : null}
        </>
      }
    >
      <AppSectionCard title="Когда">
        <AppSectionRow
          icon={TYPE_ICON[state.type]}
          title={state.typeLabel}
          subtitle="Тип события"
          onClick={() => setPicking('type')}
        />
        <AppSectionDivider />
        <AppSectionRow
          icon="schedule"
          title={state.whenLabel}
          subtitle="Дата и время"
          onClick={() => setMovingTime(true)}
        />
      </AppSectionCard>

      <AppSectionCard title="О событии">
        <AppSectionRow icon="edit" title={state.title} subtitle="Название" onClick={() => setEditing('title')} />
        <AppSectionDivider />
        {state.description ? (
          <AppSectionRow
            icon="description"
            title={state.description}
            subtitle="Описание"
            onClick={() => setEditing('description')}
          />
        ) : (
          <AppSectionRow icon="add" title="Добавить описание" action onClick={() => setEditing('description')} />
        )}
      </AppSectionCard>

      <AppSectionCard title="Дело">
        {state.caseId ? (
          <>
            <AppSectionRow
              icon="folder"
              title={state.caseTitle ?? 'Дело'}
              subtitle="Открыть дело"
              onClick={() => onOpenCase(state.caseId ?? '')}
            />
            <AppSectionDivider />
            <AppSectionRow icon="edit" title="Сменить дело" action onClick={() => setPicking('case')} />
            {/* Клиент дела: позвонить перед заседанием, не разыскивая его через дело. */}
            {state.clientId ? (
              <>
                <AppSectionDivider />
                <AppSectionRow
                  icon="person"
                  title={state.clientName ?? ''}
                  subtitle="Клиент дела"
                  onClick={() => onOpenClient(state.clientId ?? '')}
                />
              </>
            ) : null}
          </>
        ) : (
          <AppSectionRow icon="add" title="Привязать к делу" action onClick={() => setPicking('case')} />
        )}
      </AppSectionCard>
    </AppScreenScaffold>
  );
}

function TextDialog({
  title,
  value,
  onDismiss,
  onConfirm,
}: {
  title: string;
  value: string;
  onDismiss: () => void;
  onConfirm: (value: string) => void;
}) {
  const [text, setText] = useState(value);

  return (
    <AppFormDialog
      title={title}
      onDismiss={onDismiss}
      confirm={{ label: 'Готово', onClick: () => onConfirm(text) }}
    >
      <AppTextField value={text} onChange={setText} label={title} autoFocus />
    </AppFormDialog>
  );
}

/** Перенос события: дата и время вместе — заседание переносят целиком. */
function WhenDialog({
  startAt,
  onDismiss,
  onConfirm,
}: {
  startAt: number;
  onDismiss: () => void;
  onConfirm: (startAt: number) => void;
}) {
  const at = new Date(startAt);
  const pad = (value: number) => String(value).padStart(2, '0');

  const [day, setDay] = useState(
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
  );
  const [clock, setClock] = useState(`${pad(at.getHours())}:${pad(at.getMinutes())}`);

  return (
    <AppFormDialog
      title="Перенести событие"
      onDismiss={onDismiss}
      confirm={{
        label: 'Готово',
        onClick: () => {
          const moment = new Date(`${day}T${clock}`);
          if (!Number.isNaN(moment.getTime())) onConfirm(moment.getTime());
        },
      }}
    >
      <AppDateField value={day} onChange={setDay} label="Дата" />
      <AppTextField value={clock} onChange={setClock} label="Время" />
    </AppFormDialog>
  );
}
